import {
  AlarmClockIcon,
  Button,
  CalendarXmarkIcon,
  CheckIcon,
  ClockIcon,
  CloseIcon,
  cn,
  HourglassHalfIcon,
  Paragraph,
} from '@quickmd-llc/design-system'
import React from 'react'
import { type WaitingRoomV2VisitListItemDoctorApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'
import { useDailyVisit } from 'pages/WaitingRoom/daily/hooks/useDailyVisit'
import moment from 'moment'
import { useMinuteTick } from 'pages/WaitingRoom/hooks/useMinuteTick'
import { formatMinutesCompact } from './formatMinutesCompact'

// "Visit starts in" label for the current visit's own start time (blue, upcoming):
// a future day -> "In 2d"; today but >60m away -> absolute "3:10pm"; within the hour -> "In 15m".
const formatStartsIn = (visitTime: string): string => {
  const start = moment(visitTime)
  const now = moment()
  const secondsUntil = start.diff(now, 'seconds')
  // Minutes first, before the calendar-day comparison: a visit 15 minutes out that happens to
  // fall the other side of midnight is still "In 15m". Checking the day difference first read
  // it as "In 1d" for the last hour of every day.
  if (secondsUntil <= 60 * 60) return `In ${formatMinutesCompact(Math.max(1, Math.ceil(secondsUntil / 60)))}`
  const dayDiff = start.clone().startOf('day').diff(now.clone().startOf('day'), 'days')
  if (dayDiff >= 1) return `In ${dayDiff}d`
  return start.format('h:mma')
}

type Props = {
  onComplete: () => void
  onUnresolved: () => void
  onClose: () => void
  onDefer: () => void
  upcomingVisits?: WaitingRoomV2VisitListItemDoctorApiOutContract[] | undefined
}

const TimerPill = ({
  icon,
  className,
  children,
}: {
  icon: React.ReactNode
  className: string
  children: React.ReactNode
}) => (
  <div className='flex items-center gap-sp-0.25'>
    {icon}
    <Paragraph size='sm' className={cn('font-semibold', className)}>
      {children}
    </Paragraph>
  </div>
)

// Countdown to the CURRENT visit's own start — shown only while it's a scheduled visit the
// provider hasn't connected to yet. Blue up to 5m late ("Start anytime"), then amber, then red.
const renderVisitStartsIn = (visitTime: string) => {
  const secondsUntil = moment(visitTime).diff(moment(), 'seconds')
  if (secondsUntil > 0) {
    return (
      <TimerPill icon={<ClockIcon iconSize='xxs' className='text-text-highlight' />} className='text-text-highlight'>
        {formatStartsIn(visitTime)}
      </TimerPill>
    )
  }
  const lateMinutes = Math.floor(Math.abs(secondsUntil) / 60)
  if (lateMinutes < 5) {
    return (
      <TimerPill icon={<CheckIcon size='sm' className='text-text-highlight' />} className='text-text-highlight'>
        Start anytime
      </TimerPill>
    )
  }
  const color = lateMinutes < 15 ? 'text-text-warning' : 'text-text-error'
  return (
    <TimerPill icon={<AlarmClockIcon iconSize='xxs' className={color} />} className={color}>
      <span className='font-semibold'>{formatMinutesCompact(lateMinutes)}</span> late
    </TimerPill>
  )
}

// Countdown to the NEXT scheduled visit — shown once the current visit has started, is
// complete-and-reopened, or is an incomplete/pending visit. Gray up to 5m late, then amber, then red.
const renderNextVisit = (nextDateTime: string) => {
  const secondsUntil = moment(nextDateTime).diff(moment(), 'seconds')
  if (secondsUntil > 0) {
    return (
      <TimerPill icon={<HourglassHalfIcon iconSize='xxs' className='text-text-light' />} className='text-text-light'>
        Next in <span className='font-semibold'>{formatMinutesCompact(Math.ceil(secondsUntil / 60))}</span>
      </TimerPill>
    )
  }
  const lateMinutes = Math.floor(Math.abs(secondsUntil) / 60)
  let color = 'text-text-light'
  if (lateMinutes >= 15) color = 'text-text-error'
  else if (lateMinutes >= 5) color = 'text-text-warning'
  return (
    <TimerPill icon={<HourglassHalfIcon iconSize='xxs' className={color} />} className={color}>
      {lateMinutes === 0 ?
        'Next visit now'
      : <>
          Next - <span className='font-semibold'>{formatMinutesCompact(lateMinutes)}</span> late
        </>
      }
    </TimerPill>
  )
}

export const VisitRightHeader = ({ onComplete, onUnresolved, onClose, onDefer, upcomingVisits = [] }: Props) => {
  useMinuteTick()
  const { is, patientWithVisitInfo, isPrepMode } = useDailyVisit()

  const currentVisitId = patientWithVisitInfo?.VisitId
  const visitTime = patientWithVisitInfo?.VisitTime
  const isPending = patientWithVisitInfo?.VisitStatus === 'pending'
  // Show the current visit's own countdown only when it's still scheduled (not incomplete)
  // and the provider hasn't connected yet; otherwise fall back to the next-visit countdown.
  const showVisitStartsIn = is('IDLE') && !isPending && Boolean(visitTime)

  // The next scheduled visit after the one that's open (earliest upcoming that isn't current).
  const nextVisit = upcomingVisits
    .filter((v) => v.VisitId && v.VisitId !== currentVisitId && v.DateTime)
    .sort((a, b) => new Date(a.DateTime).getTime() - new Date(b.DateTime).getTime())[0]

  const timerContent = (() => {
    if (showVisitStartsIn && visitTime) return renderVisitStartsIn(visitTime)
    if (nextVisit?.DateTime) return renderNextVisit(nextVisit.DateTime)
    return null
  })()

  const isInPersonReady = patientWithVisitInfo?.VisitMethod === 'inperson'

  const visitIsWithinFiveMinutes = (() => {
    if (!visitTime) return false
    const diff = moment(visitTime).diff(moment(), 'minutes')
    return diff <= 5
  })()

  const showActions =
    isInPersonReady ||
    visitIsWithinFiveMinutes ||
    (!isPrepMode && is('IDLE', 'PROVIDER_WAS_CONNECTED_BUT_PATIENT_CONNECTION_FAILED', 'CONNECTED_CALL_ENDED'))

  const isAsync = patientWithVisitInfo?.VisitMethod === 'asynchronous'
  // In-person is "started on open" (showActions is already true via isInPersonReady), so it renders
  // the Incomplete/Unresolved/Complete set and never the bare X. See plan for the deliberate
  // deviation from Figma (in-person keeps Incomplete as its only close path; no Start-visit step).
  const started = isAsync || showActions

  return (
    <div className='relative flex items-center gap-sp-2'>
      {timerContent}
      {isAsync ?
        <Button priority='secondary' theme='warning' size='s' className='flex gap-sp-1' onClick={onDefer}>
          <CloseIcon size='sm' />
          Defer
        </Button>
      : started ?
        <Button priority='secondary' theme='warning' size='s' className='flex gap-sp-1' onClick={onClose}>
          <CloseIcon size='sm' />
          Incomplete
        </Button>
      : <Button priority='tertiary' isIcon onClick={onClose}>
          <CloseIcon />
        </Button>
      }
      {started ?
        <Button priority='secondary' theme='danger' className='flex gap-sp-1 bg-red-20' size='s' onClick={onUnresolved}>
          <CalendarXmarkIcon size='sm' />
          Unresolved
        </Button>
      : null}
      {started ?
        <Button priority='secondary' theme='success' size='s' className='flex gap-sp-1' onClick={onComplete}>
          <CheckIcon size='sm' />
          Complete
        </Button>
      : null}
    </div>
  )
}
